import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  LightBulbIcon,
  MagnifyingGlassPlusIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import MathText from '../components/MathText';
import { AppPage, BTN_PRIMARY, BTN_SECONDARY, CARD, Empty, Eyebrow, Pill, Segmented } from '../components/UI/AppUI';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.air-brief.com';
const imageUrl = (p) => (!p ? null : p.startsWith('http') ? p : `${API_URL}${p}`);

// Answer review for a finished test, filterable to just the mistakes
const Review2 = () => {
  const { examId } = useParams();
  const [examData, setExamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [position, setPosition] = useState(0);
  const [zoom, setZoom] = useState(false);

  useEffect(() => {
    if (!examId) return;
    (async () => {
      try {
        const response = await fetch(`${API_URL}/api/exams/sessions/${examId}/`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('atpl_access_token')}` },
        });
        if (response.ok) setExamData(await response.json());
        else setError('Failed to load exam data');
      } catch {
        setError('Network error occurred');
      } finally {
        setLoading(false);
      }
    })();
  }, [examId]);

  const questions = examData?.exam_questions || examData?.questions || [];
  const indexes = questions.map((_, i) => i).filter((i) => (
    filter === 'all' ? true : filter === 'wrong' ? questions[i].is_correct !== true : questions[i].is_correct === true
  ));
  const currentIndex = indexes[Math.min(position, indexes.length - 1)];
  const current = questions[currentIndex];

  // Arrow keys move between questions
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') setPosition((p) => Math.max(0, p - 1));
      if (e.key === 'ArrowRight') setPosition((p) => Math.min(indexes.length - 1, p + 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [indexes.length]);

  if (loading) {
    return <AppPage><div className="h-96 animate-pulse rounded-2xl bg-slate-200 dark:bg-white/5" /></AppPage>;
  }

  if (error || !questions.length) {
    return (
      <AppPage>
        <div className={`${CARD} mx-auto max-w-lg`}>
          <Empty icon={error ? XCircleIcon : DocumentTextIcon} title={error ? "We couldn't load this review" : 'Nothing to review'}
            action={<Link to={`/exam/${examId}/results`} className={BTN_PRIMARY}>Back to results</Link>}>
            {error || 'This test has no questions to review.'}
          </Empty>
        </div>
      </AppPage>
    );
  }

  const wrongCount = questions.filter((q) => q.is_correct !== true).length;
  const q = current?.question;
  const pickFilter = (v) => { setFilter(v); setPosition(0); };

  return (
    <AppPage>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link to={`/exam/${examId}/results`} className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900 dark:text-gray-400 dark:hover:text-white">
            <ArrowLeftIcon className="h-4 w-4" />Results
          </Link>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl dark:text-white">Review answers</h1>
          <p className="mt-1 text-slate-500 dark:text-gray-400">{examData.subject?.name}</p>
        </div>
        <Segmented label="Show" value={filter} onChange={pickFilter} options={[
          { value: 'all', label: `All (${questions.length})` },
          { value: 'wrong', label: `Wrong (${wrongCount})` },
          { value: 'correct', label: `Correct (${questions.length - wrongCount})` },
        ]} />
      </div>

      {!current ? (
        <div className={`${CARD} mt-6`}>
          <Empty icon={CheckCircleIcon} title={filter === 'wrong' ? 'No mistakes in this test' : 'Nothing here'}>
            {filter === 'wrong' ? 'Every answer was right.' : 'Try another filter.'}
          </Empty>
        </div>
      ) : (
        <div className="mt-6 grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_260px]">
          <article className={`${CARD} p-5 sm:p-7`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Eyebrow>Question {currentIndex + 1} of {questions.length}</Eyebrow>
              <Pill tone={current.is_correct ? 'green' : current.is_correct === false ? 'red' : 'gray'}>
                {current.is_correct ? <><CheckCircleIcon className="h-3.5 w-3.5" />Correct</> : current.is_correct === false ? <><XCircleIcon className="h-3.5 w-3.5" />Wrong</> : 'Not answered'}
              </Pill>
            </div>

            {q.annex_image && (
              <button type="button" onClick={() => setZoom(true)} className="group relative mt-5 block w-full overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-white/10" aria-label="Enlarge figure">
                <img src={imageUrl(q.annex_image)} alt="Question figure" className="mx-auto max-h-[36vh] object-contain" />
                <span className="absolute right-2 top-2 flex items-center gap-1 rounded-lg bg-slate-950/70 px-2 py-1 text-xs font-medium text-white"><MagnifyingGlassPlusIcon className="h-4 w-4" />Enlarge</span>
              </button>
            )}

            <p className="mt-5 whitespace-pre-line text-lg font-medium leading-relaxed text-slate-900 dark:text-white">{q.question_text}</p>

            <div className="mt-6 space-y-3">
              {q.question_type === 'USER_INPUT' ? (
                <>
                  <div className={`flex items-center gap-4 rounded-xl border px-4 py-3.5 ${current.is_correct ? 'border-emerald-500 bg-emerald-500/10' : 'border-rose-500 bg-rose-500/10'}`}>
                    <span className="text-sm text-slate-500 dark:text-gray-400">You wrote</span>
                    <span className="flex-1 font-semibold text-slate-900 dark:text-white">{current.user_input_answer || '(no answer)'}</span>
                    {current.is_correct ? <CheckCircleIcon className="h-6 w-6 text-emerald-500" /> : <XCircleIcon className="h-6 w-6 text-rose-500" />}
                  </div>
                  {!current.is_correct && (
                    <div className="flex items-center gap-4 rounded-xl border border-emerald-500 bg-emerald-500/10 px-4 py-3.5">
                      <span className="text-sm text-slate-500 dark:text-gray-400">Answer</span>
                      <span className="flex-1 font-semibold text-slate-900 dark:text-white">{q.answers?.find((a) => a.is_correct)?.answer_text || 'N/A'}</span>
                      <CheckCircleIcon className="h-6 w-6 text-emerald-500" />
                    </div>
                  )}
                </>
              ) : q.answers?.map((a, i) => {
                const picked = current.selected_answer?.id === a.id;
                const right = a.is_correct;
                const cls = right ? 'border-emerald-500 bg-emerald-500/10' : picked ? 'border-rose-500 bg-rose-500/10' : 'border-slate-200 opacity-70 dark:border-white/10';
                const tile = right ? 'bg-emerald-500 text-white' : picked ? 'bg-rose-500 text-white' : 'bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-gray-400';
                return (
                  <div key={a.id} className={`flex items-center gap-4 rounded-xl border px-4 py-3.5 ${cls}`}>
                    <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-sm font-bold ${tile}`}>{String.fromCharCode(65 + i)}</span>
                    <span className="flex-1 text-[15px] leading-snug text-slate-800 dark:text-gray-100">{a.answer_text}</span>
                    {picked && <span className="hidden text-xs font-semibold text-slate-500 sm:inline dark:text-gray-400">Your answer</span>}
                    {right && <CheckCircleIcon className="h-6 w-6 flex-shrink-0 text-emerald-500" />}
                    {picked && !right && <XCircleIcon className="h-6 w-6 flex-shrink-0 text-rose-500" />}
                  </div>
                );
              })}
            </div>

            {q.explanation?.trim() && (
              <div className="mt-6 rounded-xl bg-primary-500/10 p-4">
                <p className="flex items-center gap-2 text-sm font-semibold text-primary-800 dark:text-primary-200"><LightBulbIcon className="h-5 w-5" />Explanation</p>
                <MathText text={q.explanation} className="mt-2 block whitespace-pre-line text-sm leading-relaxed text-slate-700 dark:text-gray-300" />
              </div>
            )}

            <div className="mt-8 flex items-center justify-between gap-3 border-t border-slate-200 pt-5 dark:border-white/10">
              <button type="button" onClick={() => setPosition(Math.max(0, position - 1))} disabled={position === 0} className={`${BTN_SECONDARY} disabled:opacity-40`}>
                <ChevronLeftIcon className="h-5 w-5" /><span className="hidden sm:inline">Previous</span>
              </button>
              <span className="text-sm tabular-nums text-slate-500 dark:text-gray-400">{position + 1} / {indexes.length}</span>
              <button type="button" onClick={() => setPosition(Math.min(indexes.length - 1, position + 1))} disabled={position >= indexes.length - 1} className={`${BTN_PRIMARY} disabled:opacity-40`}>
                <span className="hidden sm:inline">Next</span><ChevronRightIcon className="h-5 w-5" />
              </button>
            </div>
          </article>

          <aside className={`${CARD} p-5 lg:sticky lg:top-24`}>
            <p className="font-semibold text-slate-900 dark:text-white">All questions</p>
            <div className="mt-4 grid grid-cols-8 gap-1.5 sm:grid-cols-10 lg:grid-cols-5">
              {questions.map((x, i) => {
                const inView = indexes.includes(i);
                const cls = x.is_correct ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : x.is_correct === false ? 'bg-rose-500/15 text-rose-700 dark:text-rose-300' : 'bg-slate-100 text-slate-500 dark:bg-white/5 dark:text-gray-400';
                return (
                  <button key={i} type="button" disabled={!inView} onClick={() => setPosition(indexes.indexOf(i))}
                    aria-label={`Question ${i + 1}`} aria-current={i === currentIndex ? 'step' : undefined}
                    className={`flex h-9 items-center justify-center rounded-lg text-xs font-semibold tabular-nums transition-opacity disabled:opacity-25 ${cls} ${i === currentIndex ? 'ring-2 ring-primary-500 ring-offset-2 ring-offset-white dark:ring-offset-slate-950' : ''}`}>
                    {i + 1}
                  </button>
                );
              })}
            </div>
            <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-gray-400">
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-emerald-500/60" />Correct</span>
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-rose-500/60" />Wrong</span>
            </div>
            {examData.subject?.id && (
              <Link to={`/subjects/${examData.subject.id}`} className={`${BTN_SECONDARY} mt-5 w-full`}>Back to subject</Link>
            )}
          </aside>
        </div>
      )}

      {zoom && q?.annex_image && (
        <div className="fixed inset-0 z-[70] flex flex-col bg-slate-950/95" onClick={() => setZoom(false)}>
          <div className="flex justify-end p-3">
            <button type="button" onClick={() => setZoom(false)} aria-label="Close" className="rounded-full bg-white/10 p-2 text-white hover:bg-white/20"><XMarkIcon className="h-6 w-6" /></button>
          </div>
          <div className="flex-1 overflow-auto p-4" onClick={(e) => e.stopPropagation()}>
            <img src={imageUrl(q.annex_image)} alt="Question figure, enlarged" className="mx-auto max-w-none rounded-lg bg-white sm:max-w-full" />
          </div>
        </div>
      )}
    </AppPage>
  );
};

export default Review2;
